/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-29 09:04:57
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { RecommendationState, RecommendationAction } from 'stores/flow-type/recommendation.type';
import { MANUAL_RECOMMENDATION_TAB_TYPE } from 'src/constants/recommendation';

import {
  GET_RECOMMENDATION_WIDGETS_REQUEST,
  GET_RECOMMENDATION_WIDGETS_SUCCESS,
  GET_RECOMMENDATION_WIDGETS_FAILURE,
  GET_RECOMMENDATION_WIDGET_REQUEST,
  GET_RECOMMENDATION_WIDGET_SUCCESS,
  GET_RECOMMENDATION_WIDGET_FAILURE,
  DELETE_RECOMMENDATION_WIDGET_REQUEST,
  DELETE_RECOMMENDATION_WIDGET_SUCCESS,
  DELETE_RECOMMENDATION_WIDGET_FAILURE,
  CREATE_RECOMMENDATION_WIDGET_REQUEST,
  CREATE_RECOMMENDATION_WIDGET_SUCCESS,
  CREATE_RECOMMENDATION_WIDGET_FAILURE,
  UPDATE_RECOMMENDATION_WIDGET_REQUEST,
  UPDATE_RECOMMENDATION_WIDGET_SUCCESS,
  UPDATE_RECOMMENDATION_WIDGET_FAILURE,
  UPDATE_RECOMMENDATION_WIDGET_ACTION_REQUEST,
  UPDATE_RECOMMENDATION_WIDGET_ACTION_SUCCESS,
  UPDATE_RECOMMENDATION_WIDGET_ACTION_FAILURE,
  DUPLICATE_RECOMMENDATION_WIDGET_REQUEST,
  DUPLICATE_RECOMMENDATION_WIDGET_SUCCESS,
  DUPLICATE_RECOMMENDATION_WIDGET_FAILURE,
  GET_MANUAL_RECOMMENDATION_REQUEST,
  GET_MANUAL_RECOMMENDATION_SUCCESS,
  GET_MANUAL_RECOMMENDATION_FAILURE,
  DELETE_MANUAL_RECOMMENDATION_REQUEST,
  DELETE_MANUAL_RECOMMENDATION_SUCCESS,
  DELETE_MANUAL_RECOMMENDATION_FAILURE,
  SEARCH_MANUAL_RECOMMENDATION_REQUEST,
  SEARCH_MANUAL_RECOMMENDATION_SUCCESS,
  SEARCH_MANUAL_RECOMMENDATION_FAILURE,
  CLEAR_SEARCH_MANUAL_RECOMMENDATION,
  REMOVE_RECOMMENDATION_FROM_SEARCH_RESULT,
  CREATE_MANUAL_RECOMMENDATION_REQUEST,
  CREATE_MANUAL_RECOMMENDATION_SUCCESS,
  CREATE_MANUAL_RECOMMENDATION_FAILURE,
  GET_MANUAL_RECOMMENDATION_BY_ID_REQUEST,
  GET_MANUAL_RECOMMENDATION_BY_ID_SUCCESS,
  GET_MANUAL_RECOMMENDATION_BY_ID_FAILURE,
  EDIT_MANUAL_RECOMMENDATION_REQUEST,
  EDIT_MANUAL_RECOMMENDATION_SUCCESS,
  EDIT_MANUAL_RECOMMENDATION_FAILURE,
  UPDATE_SEARCH_MANUAL_RECOMMENDATION_STATUS,
  ACTIVATE_AI_BASE_FAILURE,
  ACTIVATE_AI_BASE_REQUEST,
  ACTIVATE_AI_BASE_SUCCESS,
  GET_AI_MODEL_STATUSES_FAILURE,
  GET_AI_MODEL_STATUSES_REQUEST,
  GET_AI_MODEL_STATUSES_SUCCESS,
  GET_AI_MODEL_STATUS_FAILURE,
  GET_AI_MODEL_STATUS_REQUEST,
  GET_AI_MODEL_STATUS_SUCCESS,
  GET_AI_SYNC_STATUS_FAILURE,
  GET_AI_SYNC_STATUS_REQUEST,
  GET_AI_SYNC_STATUS_SUCCESS,
  GET_SYNC_ORDER_STATUS_REQUEST,
  GET_SYNC_ORDER_STATUS_SUCCESS,
  GET_SYNC_ORDER_STATUS_FAILURE,
  CHECK_ENABLE_BOOST_CORE_REQUEST,
  CHECK_ENABLE_BOOST_CORE_SUCCESS,
  CHECK_ENABLE_BOOST_CORE_FAILURE,
  GET_DEFAULT_DESIGN_SETTINGS_REQUEST,
  GET_DEFAULT_DESIGN_SETTINGS_SUCCESS,
  GET_DEFAULT_DESIGN_SETTINGS_FAILURE,
  MARK_DONE_TOUR_WIDGET_REQUEST,
  MARK_DONE_TOUR_WIDGET_SUCCESS,
  MARK_DONE_TOUR_WIDGET_FAILURE,
  SYNC_WIDGETS_REQUEST,
  SYNC_WIDGETS_SUCCESS,
  SYNC_WIDGETS_FAILURE,
  GET_RECOMMENDATION_GENERAL_SETTINGS_REQUEST,
  GET_RECOMMENDATION_GENERAL_SETTINGS_SUCCESS,
  GET_RECOMMENDATION_GENERAL_SETTINGS_FAILURE,
  UPDATE_RECOMMENDATION_GENERAL_SETTINGS_REQUEST,
  UPDATE_RECOMMENDATION_GENERAL_SETTINGS_SUCCESS,
  UPDATE_RECOMMENDATION_GENERAL_SETTINGS_FAILURE
} from 'stores/flow-type/recommendation.type';

const initialState: RecommendationState = {
  loading: false,
  saving: false,
  deleting: false,
  success: false,
  saveSuccess: false,
  error: null,

  recommendationWidget: {},
  defaultWidgetTitleSettings: {},
  recommendationWidgets: [],
  appBlockDeepLink: '',
  embedDeepLink: '',
  total: 0,

  widgetCreating: false,
  widgetCreatedSuccess: false,
  widgetId: '',

  manualHandPickedLoading: false,
  manualHandPickedLoadedSuccess: false,
  manualHandPickedRecommendations: [],
  manualHandPickedTotal: 0,

  manualSearching: false,
  manualSearchSuccess: false,
  manualRecommendationSearchResult: [],

  manualDeleting: false,
  manualDeleteSuccess: false,

  manualRecommendation: null,
  manualRecommendationLoading: false,
  manualActionLoading: false,

  recommendationTranslationFields: [],

  aimlSyncStatus: '',
  aimlModelStatus: {
    FBT: '',
    Complementary: '',
    Alternative: '',
    AIRelated: ''
  },
  syncOrderStatus: {
    current: {},
    previous: {}
  },
  checkingEnableBoostCore: false,
  isEnableBoostCore: false,
  isEnableAppBlock: false,

  markDoneTourLoading: false,
  markDoneTourSuccess: false,

  defaultDesignSettingsLoading: false,
  defaultDesignSettingsLoadedSuccess: false,
  defaultDesignSettings: {},

  widgetsSyncing: false,
  widgetSyncedSuccess: false
};
const recommendationReducers = (
  state: RecommendationState = initialState,
  action: RecommendationAction
) => {
  switch (action.type) {
    /* #region: Get recommendation widgets */
    case GET_RECOMMENDATION_WIDGETS_REQUEST:
      return {
        ...state,
        success: false,
        loading: true
      };
    case GET_RECOMMENDATION_WIDGETS_SUCCESS: {
      const { data, isOnBoarding = false } = action.payload;
      let recommendationWidgets = [];
      const isDefaultWidget = _.get(action, 'payload.data.defaultWidget', false);
      const widgets = _.get(action, 'payload.data.widgets', []);

      if (isOnBoarding) {
        recommendationWidgets = widgets;
      } else {
        recommendationWidgets = data;
      }

      // US56: Fix widget status temporarily before migrate
      recommendationWidgets = _.map(recommendationWidgets, widget => {
        return {
          ...widget,
          widgetStatus: widget.widgetStatus === 'inactive' ? 'draft' : widget.widgetStatus
        };
      });
      // End fix

      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        recommendationWidgets,
        total: recommendationWidgets.length,
        isDefaultWidget
      };
    }
    case GET_RECOMMENDATION_WIDGETS_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    /* #end-region */

    /* #region: Get recommendation widget by id */
    case GET_RECOMMENDATION_WIDGET_REQUEST:
      return {
        ...state,
        success: false,
        loading: true,
        recommendationWidget: {},
        defaultWidgetTitleSettings: {}
      };
    case GET_RECOMMENDATION_WIDGET_SUCCESS: {
      const { data = {} } = action.payload;
      const { widgetSettings: recommendationWidget = {}, defaultWidgetTitleSettings = {} } = data;

      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        recommendationWidget,
        defaultWidgetTitleSettings
      };
    }
    case GET_RECOMMENDATION_WIDGET_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    /* #end-region */

    /* #region: Delete recommendation widget */
    case DELETE_RECOMMENDATION_WIDGET_REQUEST:
      return {
        ...state,
        success: false,
        deleting: true
      };
    case DELETE_RECOMMENDATION_WIDGET_SUCCESS: {
      return {
        ...state,
        deleting: false,
        success: true,
        total: state.total - 1,
        error: undefined
      };
    }
    case DELETE_RECOMMENDATION_WIDGET_FAILURE:
      return {
        ...state,
        deleting: false,
        success: false,
        error: action.payload
      };
    /* #end-region */

    /* #region: Create recommendation widget */
    case CREATE_RECOMMENDATION_WIDGET_REQUEST:
      return {
        ...state,
        widgetCreatedSuccess: false,
        widgetId: '',
        widgetCreating: true
      };
    case CREATE_RECOMMENDATION_WIDGET_SUCCESS: {
      const { data = {} } = action.payload;
      const {
        widgetSettings: recommendationWidget = {},
        defaultWidgetTitleSettings = {},
        appBlockDeepLink,
        embedDeepLink
      } = data;
      const widgetId = _.get(recommendationWidget, 'id', '');

      return {
        ...state,
        widgetCreating: false,
        widgetCreatedSuccess: true,
        widgetId: widgetId,
        recommendationWidget,
        defaultWidgetTitleSettings,
        appBlockDeepLink,
        embedDeepLink,
        error: undefined
      };
    }
    case CREATE_RECOMMENDATION_WIDGET_FAILURE:
      return {
        ...state,
        widgetCreating: false,
        widgetCreatedSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Update recommendation widget */
    case UPDATE_RECOMMENDATION_WIDGET_REQUEST:
      return {
        ...state,
        saveSuccess: false,
        saving: true
      };
    case UPDATE_RECOMMENDATION_WIDGET_SUCCESS: {
      const { data = {} } = action.payload;
      const {
        widgetSettings: recommendationWidget = {},
        defaultWidgetTitleSettings = {},
        appBlockDeepLink,
        embedDeepLink
      } = data;
      const widgetId = _.get(recommendationWidget, 'id', '');

      return {
        ...state,
        saving: false,
        saveSuccess: true,
        error: undefined,
        recommendationWidget,
        widgetId,
        defaultWidgetTitleSettings,
        appBlockDeepLink,
        embedDeepLink
      };
    }
    case UPDATE_RECOMMENDATION_WIDGET_FAILURE:
      return {
        ...state,
        saving: false,
        saveSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Update recommendation widget status */
    case UPDATE_RECOMMENDATION_WIDGET_ACTION_REQUEST:
      return {
        ...state,
        saveSuccess: false,
        saving: true
      };
    case UPDATE_RECOMMENDATION_WIDGET_ACTION_SUCCESS: {
      const { data = {} } = action.payload;
      const actionURL = _.get(data, 'actionURL', '');

      return {
        ...state,
        saving: false,
        saveSuccess: true,
        error: undefined,
        actionURL
      };
    }
    case UPDATE_RECOMMENDATION_WIDGET_ACTION_FAILURE:
      return {
        ...state,
        saving: false,
        saveSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Duplicate recommendation widget */
    case DUPLICATE_RECOMMENDATION_WIDGET_REQUEST:
      return {
        ...state,
        widgetDuplicatedSuccess: false,
        widgetId: '',
        widgetDuplicating: true,
        recommendationWidget: {}
      };
    case DUPLICATE_RECOMMENDATION_WIDGET_SUCCESS: {
      const { data = {} } = action.payload;
      const widgetId = _.get(data, 'id', '');

      return {
        ...state,
        widgetDuplicating: false,
        widgetDuplicatedSuccess: true,
        widgetId: widgetId,
        recommendationWidget: data,
        error: undefined
      };
    }
    case DUPLICATE_RECOMMENDATION_WIDGET_FAILURE:
      return {
        ...state,
        widgetDuplicating: false,
        widgetDuplicatedSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Get manual recommendation */
    case GET_MANUAL_RECOMMENDATION_REQUEST: {
      const { type } = action.payload;
      let updatedState = {};

      switch (type) {
        case MANUAL_RECOMMENDATION_TAB_TYPE.HAND_PICKED_PRODUCTS:
          updatedState = {
            manualHandPickedLoadedSuccess: false,
            manualHandPickedLoading: true,
            manualHandPickedTotal: 0
          };
          break;
        default:
          break;
      }
      return {
        ...state,
        ...updatedState
      };
    }
    case GET_MANUAL_RECOMMENDATION_SUCCESS: {
      const { data = {}, type } = action.payload;
      const { data: result, total } = data;
      let updatedState = {};
      switch (type) {
        case MANUAL_RECOMMENDATION_TAB_TYPE.HAND_PICKED_PRODUCTS:
          updatedState = {
            manualHandPickedLoading: false,
            manualHandPickedLoadedSuccess: true,
            manualHandPickedRecommendations: result,
            manualHandPickedTotal: total
          };
          break;
        default:
          break;
      }
      return {
        ...state,
        ...updatedState,
        error: undefined
      };
    }
    case GET_MANUAL_RECOMMENDATION_FAILURE: {
      const { type } = action.payload;
      let updatedState = {};

      switch (type) {
        case MANUAL_RECOMMENDATION_TAB_TYPE.HAND_PICKED_PRODUCTS:
          updatedState = {
            manualHandPickedLoading: false,
            manualHandPickedLoadedSuccess: false,
            manualHandPickedTotal: 0
          };
          break;
        default:
          break;
      }
      return {
        ...state,
        ...updatedState,
        error: action.payload
      };
    }
    /* #end-region */

    /* #region: Search manual recommendation */
    case SEARCH_MANUAL_RECOMMENDATION_REQUEST:
      return {
        ...state,
        manualSearchSuccess: false,
        manualSearching: true
      };
    case SEARCH_MANUAL_RECOMMENDATION_SUCCESS: {
      const { data = {} } = action.payload;
      const { data: result } = data;
      return {
        ...state,
        manualSearching: false,
        manualSearchSuccess: true,
        error: undefined,
        manualRecommendationSearchResult: result
      };
    }
    case SEARCH_MANUAL_RECOMMENDATION_FAILURE:
      return {
        ...state,
        manualSearching: false,
        manualSearchSuccess: false,
        error: action.payload
      };
    case CLEAR_SEARCH_MANUAL_RECOMMENDATION:
      return {
        ...state,
        manualSearching: false,
        manualSearchSuccess: false,
        manualRecommendationSearchResult: [],
        error: undefined
      };
    case REMOVE_RECOMMENDATION_FROM_SEARCH_RESULT:
      const deletedRecommendationId = action.payload;
      let stateManualRecommendationSearchResult = state.manualRecommendationSearchResult || [];

      if (!_.isEmpty(stateManualRecommendationSearchResult)) {
        stateManualRecommendationSearchResult = stateManualRecommendationSearchResult.filter(
          item => item.id !== deletedRecommendationId
        );
      }
      return {
        ...state,
        manualRecommendationSearchResult: stateManualRecommendationSearchResult
      };
    case UPDATE_SEARCH_MANUAL_RECOMMENDATION_STATUS:
      return {
        ...state,
        manualSearchSuccess: action.payload
      };
    /* #end-region */

    /* #region: Delete manual recommendation */
    case DELETE_MANUAL_RECOMMENDATION_REQUEST:
      return {
        ...state,
        manualDeleteSuccess: false,
        manualDeleting: true
      };
    case DELETE_MANUAL_RECOMMENDATION_SUCCESS: {
      return {
        ...state,
        manualDeleting: false,
        manualDeleteSuccess: true,
        error: undefined
      };
    }
    case DELETE_MANUAL_RECOMMENDATION_FAILURE:
      return {
        ...state,
        manualDeleting: false,
        manualDeleteSuccess: false,
        error: action.payload
      };

    /* #region: Create manual recommendation */
    case CREATE_MANUAL_RECOMMENDATION_REQUEST:
      return {
        ...state,
        manualActionLoading: true
      };
    case CREATE_MANUAL_RECOMMENDATION_SUCCESS: {
      return {
        ...state,
        manualActionLoading: false,
        error: undefined
      };
    }
    case CREATE_MANUAL_RECOMMENDATION_FAILURE:
      return {
        ...state,
        manualActionLoading: false,
        error: action.payload
      };
    /* #end-region */

    /* #region: Get manual recommendation by id */
    case GET_MANUAL_RECOMMENDATION_BY_ID_REQUEST:
      return {
        ...state,
        manualRecommendationLoading: true,
        manualRecommendation: null
      };
    case GET_MANUAL_RECOMMENDATION_BY_ID_SUCCESS: {
      return {
        ...state,
        manualRecommendationLoading: false,
        manualRecommendation: action.payload,
        error: undefined
      };
    }
    case GET_MANUAL_RECOMMENDATION_BY_ID_FAILURE:
      return {
        ...state,
        manualRecommendationLoading: false,
        manualRecommendation: null,
        error: action.payload
      };
    /* #end-region */

    /* #region: Edit manual recommendation */
    case EDIT_MANUAL_RECOMMENDATION_REQUEST:
      return {
        ...state,
        manualActionLoading: true
      };
    case EDIT_MANUAL_RECOMMENDATION_SUCCESS: {
      return {
        ...state,
        manualActionLoading: false,
        error: undefined
      };
    }
    case EDIT_MANUAL_RECOMMENDATION_FAILURE:
      return {
        ...state,
        manualActionLoading: false,
        error: action.payload
      };
    /* #end-region */

    /* #region: Trigger sync aiml */
    case ACTIVATE_AI_BASE_REQUEST:
      return { ...state };
    case ACTIVATE_AI_BASE_SUCCESS: {
      const { data = {} } = action.payload;
      const { status } = data;
      return {
        ...state,
        aimlSyncStatus: status
      };
    }
    case ACTIVATE_AI_BASE_FAILURE:
      return {
        ...state,
        error: action.payload
      };
    /* #end-region */

    /* #region: Get aiml sync status */
    case GET_AI_SYNC_STATUS_REQUEST:
      return { ...state };
    case GET_AI_SYNC_STATUS_SUCCESS: {
      const { data = {} } = action.payload;
      const { status } = data;
      return {
        ...state,
        aimlSyncStatus: status
      };
    }
    case GET_AI_SYNC_STATUS_FAILURE:
      return {
        ...state,
        error: action.payload
      };
    /* #end-region */

    /* #region: Get all aiml models status */
    case GET_AI_MODEL_STATUSES_REQUEST:
      return { ...state };
    case GET_AI_MODEL_STATUSES_SUCCESS: {
      const { data = [] } = action.payload;
      const modelStatus = _.reduce(
        data,
        (acc, { modelType, status, previousTime }) => ({
          ...acc,
          [modelType]: previousTime
            ? 'DONE'
            : status !== 'DONE' && status !== 'NOT_ENOUGH'
            ? 'PROCESSING'
            : status
        }),
        {}
      );
      return {
        ...state,
        aimlModelStatus: modelStatus
      };
    }
    case GET_AI_MODEL_STATUSES_FAILURE:
      return {
        ...state,
        error: action.payload
      };
    /* #end-region */

    /* #region: Get aiml model status by Type */
    case GET_AI_MODEL_STATUS_REQUEST:
      return { ...state };
    case GET_AI_MODEL_STATUS_SUCCESS: {
      const { data = {}, type = '' } = action.payload;
      const { aimlModelStatus } = state;
      aimlModelStatus[type] = data.previousTime
        ? 'DONE'
        : data.status !== 'DONE' && data.status !== 'NOT_ENOUGH'
        ? 'PROCESSING'
        : data.status;
      return {
        ...state,
        aimlModelStatus
      };
    }
    case GET_AI_MODEL_STATUS_FAILURE:
      return {
        ...state,
        error: action.payload
      };
    /* #end-region */
    /* #region: Get aiml model status by Type */
    case GET_SYNC_ORDER_STATUS_REQUEST:
      return { ...state };
    case GET_SYNC_ORDER_STATUS_SUCCESS: {
      const syncOrderStatus = action.payload;
      return {
        ...state,
        syncOrderStatus
      };
    }
    case GET_SYNC_ORDER_STATUS_FAILURE:
      return {
        ...state,
        error: action.payload
      };
    /* #end-region */

    /* #region: check theme already enabling boost core */
    case CHECK_ENABLE_BOOST_CORE_REQUEST:
      return {
        ...state,
        checkingEnableBoostCore: true
      };
    case CHECK_ENABLE_BOOST_CORE_SUCCESS: {
      const { isEnableBoostCore = false, isEnableAppBlock = false } = action.payload;
      return {
        ...state,
        checkingEnableBoostCore: false,
        isEnableBoostCore,
        isEnableAppBlock
      };
    }
    case CHECK_ENABLE_BOOST_CORE_FAILURE:
      return {
        ...state,
        checkingEnableBoostCore: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: mark done tour widget guide */
    case MARK_DONE_TOUR_WIDGET_REQUEST:
      return {
        ...state,
        markDoneTourLoading: true,
        markDoneTourSuccess: false
      };
    case MARK_DONE_TOUR_WIDGET_SUCCESS: {
      return {
        ...state,
        markDoneTourLoading: false,
        markDoneTourSuccess: true
      };
    }
    case MARK_DONE_TOUR_WIDGET_FAILURE:
      return {
        ...state,
        markDoneTourLoading: false,
        markDoneTourSuccess: false,
        error: action.payload
      };

    /* #endregion */

    /* #region: Get default design settings */
    case GET_DEFAULT_DESIGN_SETTINGS_REQUEST:
      return {
        ...state,
        defaultDesignSettingsLoading: true,
        defaultDesignSettingsLoadedSuccess: false
      };
    case GET_DEFAULT_DESIGN_SETTINGS_SUCCESS: {
      const defaultWidgetTitleSettings = _.pick(action.payload, [
        'titleAlignment',
        'titleFont',
        'titleFontSize',
        'titleFontStyle',
        'titleTextColor',
        'titleTextTransform',
        'templateType'
      ]);
      return {
        ...state,
        defaultDesignSettingsLoading: false,
        defaultDesignSettingsLoadedSuccess: true,
        defaultWidgetTitleSettings
      };
    }
    case GET_DEFAULT_DESIGN_SETTINGS_FAILURE:
      return {
        ...state,
        defaultDesignSettingsLoading: false,
        defaultDesignSettingsLoadedSuccess: true,
        error: action.payload
      };
    /* #endregion */

    /* #region: Get recommendation widgets */
    case SYNC_WIDGETS_REQUEST:
      return {
        ...state,
        widgetsSyncedSuccess: false,
        widgetsSyncing: true
      };
    case SYNC_WIDGETS_SUCCESS: {
      const widgets = _.get(action, 'payload.widgets', []);
      return {
        ...state,
        widgetsSyncing: false,
        widgetsSyncedSuccess: true,
        error: undefined,
        recommendationWidgets: widgets,
        total: widgets.length
      };
    }
    case SYNC_WIDGETS_FAILURE:
      return {
        ...state,
        widgetsSyncing: false,
        widgetsSyncedSuccess: false,
        error: action.payload
      };
    /* #endregion */
    /* #region: Get general settings */
    case GET_RECOMMENDATION_GENERAL_SETTINGS_REQUEST:
      return {
        ...state,
        recommendationGeneralSettingLoading: true,
        recommendationGeneralSettingSuccess: false
      };
    case GET_RECOMMENDATION_GENERAL_SETTINGS_SUCCESS: {
      const recommendationGeneralSettings = action.payload;
      return {
        ...state,
        recommendationGeneralSettingLoading: false,
        recommendationGeneralSettingSuccess: true,
        recommendationGeneralSettings
      };
    }
    case GET_RECOMMENDATION_GENERAL_SETTINGS_FAILURE:
      return {
        ...state,
        recommendationGeneralSettingLoading: false,
        recommendationGeneralSettingSuccess: true,
        error: action.payload
      };
    /* #endregion */
    /* #region: Update general settings */
    case UPDATE_RECOMMENDATION_GENERAL_SETTINGS_REQUEST:
      return {
        ...state,
        updateRecommendationGeneralSettingLoading: true,
        updateRecommendationGeneralSettingSuccess: false
      };
    case UPDATE_RECOMMENDATION_GENERAL_SETTINGS_SUCCESS: {
      const recommendationGeneralSettings = action.payload;
      return {
        ...state,
        updateRecommendationGeneralSettingLoading: false,
        updateRecommendationGeneralSettingSuccess: true,
        recommendationGeneralSettings
      };
    }
    case UPDATE_RECOMMENDATION_GENERAL_SETTINGS_FAILURE:
      return {
        ...state,
        updateRecommendationGeneralSettingLoading: false,
        updateRecommendationGeneralSettingSuccess: true,
        error: action.payload
      };
    /* #endregion */
    default:
      return state;
  }
};
export default recommendationReducers;
